import { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { useToast } from "@/client/components/common/toast"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import type { PointBalance, PointTransaction, TransactionType } from "@/lib/supabase/types"
import styles from "./wallet-page.module.css"

type FilterTab = "all" | "income" | "expense"

interface TransactionMeta {
  label: string
  icon: "income" | "expense" | "frozen"
  sign: "+" | "-" | ""
}

const TRANSACTION_META: Record<TransactionType, TransactionMeta> = {
  earn: { label: "收入", icon: "income", sign: "+" },
  deposit: { label: "充值", icon: "income", sign: "+" },
  refund: { label: "退款", icon: "income", sign: "+" },
  unfreeze: { label: "解冻", icon: "income", sign: "+" },
  escrow_release: { label: "托管释放", icon: "income", sign: "+" },
  withdraw: { label: "提现", icon: "expense", sign: "-" },
  consume: { label: "消费", icon: "expense", sign: "-" },
  platform_fee: { label: "平台费", icon: "expense", sign: "-" },
  freeze: { label: "冻结", icon: "frozen", sign: "" },
}

const INCOME_TYPES = new Set<TransactionType>(["earn", "deposit", "refund", "unfreeze", "escrow_release"])
const EXPENSE_TYPES = new Set<TransactionType>(["withdraw", "consume", "platform_fee", "freeze"])

function formatAmount(amount: number): string {
  return amount.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function TransactionIcon({ type }: { type: TransactionType }) {
  const meta = TRANSACTION_META[type]
  if (meta.icon === "income") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
      </svg>
    )
  }
  if (meta.icon === "expense") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <polyline points="19 12 12 19 5 12" />
      </svg>
    )
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

export default function WalletPage() {
  const { user } = useAuthStore()
  const toast = useToast()
  const navigate = useNavigate()

  const [balance, setBalance] = useState<PointBalance | null>(null)
  const [transactions, setTransactions] = useState<PointTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<FilterTab>("all")

  useEffect(() => {
    async function fetchData() {
      const supabase = getBrowserSupabaseClient()
      if (!supabase || !user) return

      try {
        const [balanceRes, txRes] = await Promise.all([
          supabase
            .from("point_balances")
            .select("*")
            .eq("user_id", user.id)
            .single(),
          supabase
            .from("point_transactions")
            .select("*")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false })
            .limit(20),
        ])

        if (balanceRes.data) {
          setBalance(balanceRes.data as PointBalance)
        }
        if (txRes.data) {
          setTransactions(txRes.data as PointTransaction[])
        }
      } catch {
        toast.error("加载钱包数据失败")
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [user, toast])

  const filtered = useMemo(() => {
    if (activeTab === "all") return transactions
    return transactions.filter((tx) =>
      activeTab === "income" ? INCOME_TYPES.has(tx.type) : EXPENSE_TYPES.has(tx.type),
    )
  }, [transactions, activeTab])

  const stats = useMemo(() => {
    let income = 0
    let expense = 0
    for (const tx of transactions) {
      if (INCOME_TYPES.has(tx.type)) income += tx.amount
      if (EXPENSE_TYPES.has(tx.type)) expense += tx.amount
    }
    return { income, expense, frozen: balance?.frozen ?? 0 }
  }, [transactions, balance])

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>钱包</span>
        </div>
        <div className={styles.loadingHero}>
          <LoadingSkeleton variant="text" width="40%" height={14} />
          <LoadingSkeleton variant="text" width="60%" height={32} style={{ marginTop: 12 }} />
          <LoadingSkeleton variant="text" width="30%" height={14} style={{ marginTop: 8 }} />
        </div>
        <div className={styles.loadingStats}>
          <div className={styles.loadingStat} />
          <div className={styles.loadingStat} />
          <div className={styles.loadingStat} />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className={styles.loadingItem}>
            <LoadingSkeleton variant="circular" width={40} height={40} />
            <div style={{ flex: 1 }}>
              <LoadingSkeleton variant="text" width="50%" height={14} />
              <LoadingSkeleton variant="text" width="70%" height={12} style={{ marginTop: 6 }} />
            </div>
          </div>
        ))}
      </div>
    )
  }

  const currentBalance = balance?.balance ?? 0
  const frozenAmount = balance?.frozen ?? 0

  return (
    <div className={styles.page}>
      {/* Sticky header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="返回">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className={styles.headerTitle}>钱包</span>
      </div>

      {/* Hero balance card */}
      <div className={styles.heroCard}>
        <div className={styles.balanceLabel}>账户余额 (BBP)</div>
        <div className={styles.balanceValue}>¥{formatAmount(currentBalance)}</div>
        <div className={styles.frozenInfo}>冻结: ¥{formatAmount(frozenAmount)}</div>
        <div className={styles.actionRow}>
          <button
            className={`${styles.actionButton} ${styles.depositBtn}`}
            onClick={() => toast.info("功能即将上线")}
          >
            充值
          </button>
          <button
            className={`${styles.actionButton} ${styles.withdrawBtn}`}
            onClick={() => toast.info("功能即将上线")}
          >
            提现
          </button>
        </div>
      </div>

      {/* Quick stats */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <span className={styles.statValueIncome}>¥{formatAmount(stats.income)}</span>
          <span className={styles.statLabel}>收入</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValueExpense}>¥{formatAmount(stats.expense)}</span>
          <span className={styles.statLabel}>支出</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValueFrozen}>¥{formatAmount(stats.frozen)}</span>
          <span className={styles.statLabel}>冻结</span>
        </div>
      </div>

      {/* Section header + tab filter */}
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>交易记录</span>
      </div>

      <div className={styles.tabBar}>
        {(["all", "income", "expense"] as const).map((tab) => (
          <button
            key={tab}
            className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "all" ? "全部" : tab === "income" ? "收入" : "支出"}
          </button>
        ))}
      </div>

      {/* Transaction list */}
      <div className={styles.transactionList}>
        {filtered.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <span className={styles.emptyText}>暂无交易记录</span>
          </div>
        ) : (
          filtered.map((tx) => {
            const meta = TRANSACTION_META[tx.type]
            const isIncome = meta.icon === "income"
            const amountStr = `${meta.sign}¥${formatAmount(tx.amount)}`

            return (
              <div key={tx.id} className={styles.transactionItem}>
                <div
                  className={`${styles.transactionIcon} ${
                    styles[`icon${meta.icon.charAt(0).toUpperCase()}${meta.icon.slice(1)}`]
                  }`}
                >
                  <TransactionIcon type={tx.type} />
                </div>
                <div className={styles.transactionInfo}>
                  <span className={styles.transactionTitle}>{meta.label}</span>
                  <span className={styles.transactionDesc}>
                    {tx.description || meta.label}
                  </span>
                </div>
                <div className={styles.transactionRight}>
                  <span
                    className={`${styles.transactionAmount} ${
                      isIncome ? styles.amountIncome : styles.amountExpense
                    }`}
                  >
                    {amountStr}
                  </span>
                  <span className={styles.transactionDate}>{formatDate(tx.created_at)}</span>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
